import { EChangeOperation, isTrackedClass, ITrackedArray, ITrackedClass } from '../../interfaces/tracked-class';
import { SyncService } from '../../services/sync-service';
import { history } from '../history';
import { changesProcessed } from '../utilities';

export function nextTick() {
    changesProcessed.next();
}

const __arrayProtoFunctions: any = {};
['push', 'unshift', 'shift', 'pop', 'splice', 'reorder', 'clear'].forEach((arrayFunc: any) => {
    __arrayProtoFunctions[arrayFunc] = Array.prototype[arrayFunc];

    // We shouldn't need this code after the Vue update to 3.2. Was needed to fix reactivity on arrays for Vue
    // Delete this when no confirmed that there are no issues with reactivity in creator
    // Array.prototype[arrayFunc] = function(...args: any) {
    //     if(isTrackedArray(this)) {
    //         return TrackedArray.prototype[arrayFunc].apply(this, args);
    //     } else {
    //         return __arrayProtoFunctions[arrayFunc].apply(this, args);
    //     }
    // }
});

class TrackedArray<A, T> extends Array implements ITrackedArray<A> {
    $isTracked = true;

    constructor(
        private parentObj: T & ITrackedClass,
        private propertyName: string,
        private changesIgnored: boolean,
        private historyIgnored: boolean,
        source?: A[]
    ) {
        super();

        if (source) {
            for (let i = 0; i < source.length; i++) {
                __arrayProtoFunctions.push.call(this, source[i]);
            }
        }
    }

    push(...items: A[]) {
        if (!this.changesIgnored) {
            this.parentObj.setPropertyIsModified(this.propertyName, true);
            if (!this.historyIgnored) {
                history.changeEvents(
                    this,
                    items.map(item => ({
                        property: this.length,
                        operation: EChangeOperation.Push,
                        value: item,
                        originalValue: undefined,
                    }))
                );
            }
        }

        const res = __arrayProtoFunctions.push.apply(this, items); // super.push(...items);
        for (const item of items) {
            this.parentObj.addedToChildArray(this.parentObj, item, this.propertyName);
        }
        return res;
    }

    unshift(...items: A[]) {
        if (!this.changesIgnored) {
            this.parentObj.setPropertyIsModified(this.propertyName, true);
            if (!this.historyIgnored) {
                history.changeEvents(
                    this,
                    items.map(item => ({
                        property: 0,
                        operation: EChangeOperation.Push,
                        value: item,
                        originalValue: undefined,
                    }))
                );
            }
        }
        const res = __arrayProtoFunctions.unshift.apply(this, items); // super.unshift(...items);

        for (const item of items) {
            this.parentObj.addedToChildArray(this.parentObj, item, this.propertyName);
        }

        return res;
    }

    shift(destroy?: boolean): A {
        const result = __arrayProtoFunctions.shift.apply(this); // super.shift();
        if (!this.changesIgnored) {
            this.parentObj.setPropertyIsModified(this.propertyName, true);
            if (!this.historyIgnored) {
                history.changeEvent(this, 0, undefined, result, EChangeOperation.Shift);
            }
        }
        if (destroy && isTrackedClass(result)) {
            result.destroy();
        }
        this.parentObj.addedToChildArray(this.parentObj, result, this.propertyName);

        return result;
    }

    pop(destroy?: boolean): A {
        const result = __arrayProtoFunctions.pop.apply(this); // super.pop();
        if (!this.changesIgnored) {
            this.parentObj.setPropertyIsModified(this.propertyName, true);
            if (!this.historyIgnored) {
                history.changeEvent(this, this.length, undefined, result, EChangeOperation.Pop);
            }
        }
        if (destroy && isTrackedClass(result)) {
            result.destroy();
        }
        this.parentObj.addedToChildArray(this.parentObj, result, this.propertyName);
        return result;
    }

    splice(start: number, deleteCount: number | undefined, ...insert: A[]): A[] {
        // const result = Array.prototype.splice.call(array, start, deleteCount || 0, ...insert);
        let result: A[];
        if (insert.length === 0) {
            result = __arrayProtoFunctions.splice.apply(this, [start, deleteCount]); // super.splice(start, deleteCount);
        } else {
            result = __arrayProtoFunctions.splice.apply(this, [start, deleteCount || 0, ...insert]); // super.splice(start, deleteCount || 0, ...insert);
        }

        result.forEach(resultItem => {
            if (isTrackedClass(resultItem)) {
                resultItem.destroy();
            }
        });
        for (const item of result) {
            this.parentObj.removedFromChildArray(this.parentObj, item, this.propertyName);
        }
        for (const item of insert) {
            this.parentObj.addedToChildArray(this.parentObj, item, this.propertyName);
        }

        if (!this.changesIgnored) {
            this.parentObj.setPropertyIsModified(this.propertyName, true);
            if (!this.historyIgnored) {
                history.changeEvent(this, start, insert, result, EChangeOperation.Splice);
            }
        }
        return result;
    }

    remove(item: A): A[] {
        const index = this.indexOf(item);
        if (index > -1) {
            return this.splice(index, 1);
        }
        return [];
    }

    reorder(fromIndex: number, toIndex: number, undo: boolean) {
        __arrayProtoFunctions.reorder.apply(this, [fromIndex, toIndex, undo]);

        if (!this.changesIgnored) {
            this.parentObj.setPropertyIsModified(this.propertyName, true);
        }
    }
}

/** Decorator to track the properties of a class. Used in conjunction with the change tracker class.
 * Properties with this decorator will be watched by the change tracker
 * @param {Object}   options          decorator options object
 * @param {Function} options.onCreate function that is called when a new class instance is created. Called with the proxy object that was created.
 */
export function TrackChanges<T = any>(options?: TrackChangesOptions<T>) {
    const onCreateCallback = options ? options.onCreate : undefined;
    const isPropertyEqualOverride = options ? options.isPropertyEqual : undefined;
    const onDestroy = options ? options.onDestroy : undefined;
    const addedToChildArray = options ? options.addedToChildArray : undefined;
    const removedFromChildArray = options ? options.removedFromChildArray : undefined;

    return <C extends { new (...args: any[]): any }>(targetClass: C) => {
        const TrackedClass = class extends targetClass implements ITrackedClass {
            get $changesTracked() {
                return true;
            }

            setPropertyIsModified() {}
            destroy() {}

            constructor(...args: any[]) {
                super(...args);
                const trackingState: { isModified: boolean; baseObject: any } = {
                    baseObject: this,
                    isModified: false,
                };

                if (typeof this.originalValues === 'undefined') {
                    Object.defineProperties(this, {
                        changes: {
                            value: new Map<string, any>(),
                            enumerable: false,
                            writable: false,
                            configurable: false,
                        },
                        originalValues: {
                            value: new Map<string, any>(),
                            enumerable: false,
                            writable: false,
                            configurable: false,
                        },
                        _trackChanges: {
                            value: false,
                            enumerable: false,
                            writable: true,
                            configurable: false,
                        },
                        isModified: {
                            configurable: false,
                            enumerable: false,
                            set: () => {},
                            get: () => trackingState.isModified,
                        },
                        commitChanges: {
                            configurable: false,
                            enumerable: false,
                            writable: false,
                            value: (inherited = false) => commitChanges(this, inherited),
                        },
                        setPropertyIsModified: {
                            configurable: false,
                            enumerable: false,
                            writable: false,
                            value: setPropertyIsModified,
                        },
                        destroy: {
                            configurable: false,
                            enumerable: false,
                            writable: false,
                            value: () => destroy(this),
                        },
                        trackChanges: {
                            configurable: false,
                            enumerable: false,
                            writable: false,
                            value: (active: boolean) => (this._trackChanges = active),
                        },
                        rawNode: {
                            configurable: false,
                            enumerable: false,
                            writable: false,
                            value: trackingState.baseObject,
                        },
                    });
                }

                const changedValuesMap: Map<string, any> = this.changes;
                const originalValuesMap: Map<string, any> = this.originalValues;

                const proxy = new Proxy(this, {
                    // get: (target, property) => {
                    //     if (property === 'isModified') {
                    //         return trackingState.isModified;
                    //     } else if (property === 'commitChanges') {
                    //         return (inherited: boolean = false) => commitChanges(target, inherited);
                    //     } else if (property === 'setPropertyIsModified') {
                    //         return setPropertyIsModified;
                    //     } else if (property === 'addedToChildArray') {
                    //         return addedToChildArray ? addedToChildArray : () => { }
                    //     } else if (property === 'removedFromChildArray') {
                    //         return removedFromChildArray ? removedFromChildArray : () => { }
                    //     } else if (property === 'destroy') {
                    //         return () => destroy(target);
                    //     } else if (property === 'trackChanges') {
                    //         return (active: boolean) => this._trackChanges = active;
                    //     } else if (property === 'rawNode') {
                    //         return trackingState.baseObject;
                    //     }
                    //     return target[property as string];
                    // },
                    set: (target, property, newValue) => {
                        // Allow clearing changes by setting is modified to false.
                        if (property === 'isModified') {
                            if (newValue === false && newValue !== trackingState.isModified) {
                                originalValuesMap.clear();
                                changedValuesMap.clear();
                                updateIsModified();
                            } else if (newValue === true) {
                                for (const key in target) {
                                    setPropertyIsModified(key, true);
                                }
                            }
                            return true;
                            // Throw type error on trying to redefine decorator functions
                        } else if (property === 'setPropertyIsModified') {
                            return false;
                        } else {
                            // If object is not being tracked, just set the value without tracking the changes
                            if (!this._trackChanges) {
                                return Reflect.set(trackingState.baseObject, property, newValue);
                            }

                            const changesIgnored =
                                Reflect.has(target, '_changesIgnoredProperties') &&
                                Reflect.get(target, '_changesIgnoredProperties').has(property);
                            const historyIgnored =
                                Reflect.has(target, '_historyIgnoredProperties') &&
                                Reflect.get(target, '_historyIgnoredProperties').has(property);

                            // If the value is an array, then we have to proxy the array to pick up new array elements
                            // Important that this occurs even if the object is not being tracked because
                            // parent node management will break without the array proxy
                            if (Array.isArray(newValue)) {
                                newValue = new TrackedArray(
                                    proxy,
                                    property.toString(),
                                    changesIgnored,
                                    historyIgnored,
                                    newValue
                                );
                            }

                            if (!changesIgnored) {
                                const originalValue = originalValuesMap.get(property.toString());
                                const currentValue = Reflect.get(target, property);

                                // If the new value is the same as previous, do nothing.
                                if (
                                    isPropertyEqualOverride &&
                                    isPropertyEqualOverride(currentValue, newValue, property as any)
                                ) {
                                    return true;
                                } else if (currentValue === newValue) {
                                    return true;
                                }

                                // Skip checks if originalValue is null. It means isModified is forced to be true until node is processed.
                                if (originalValue !== null) {
                                    if (!originalValue) {
                                        // If the value is changed and was never changed before, register the change
                                        originalValuesMap.set(property.toString(), currentValue);
                                    } else {
                                        let isPropertyModified: boolean;
                                        // If the value was changed before, see if it's the same as the original value.
                                        let isEqualToOriginal: boolean | undefined;
                                        if (isPropertyEqualOverride) {
                                            isEqualToOriginal = isPropertyEqualOverride(
                                                originalValue,
                                                newValue,
                                                property as any
                                            );
                                        }
                                        if (typeof isEqualToOriginal !== 'undefined') {
                                            isPropertyModified = !isEqualToOriginal;
                                        } else {
                                            isPropertyModified = originalValue !== newValue;
                                        }

                                        // If the value is the same as the original, de-register the change
                                        if (!isPropertyModified) {
                                            originalValuesMap.delete(property.toString());
                                        }
                                    }

                                    updateIsModified();
                                }
                            }
                            if (originalValuesMap.has(property.toString())) {
                                changedValuesMap.set(property.toString(), newValue);
                            } else {
                                changedValuesMap.delete(property.toString());
                            }

                            return Reflect.set(trackingState.baseObject, property, newValue);
                        }
                    },
                });
                // Don't register changes in the history when creating new objects as they have to be registered as new objects instead.
                history.ignoreUntilStable(proxy);

                // The sync service won't have the right proxy object when a new object is created
                SyncService.registerNewObject(proxy);

                // The properties set on the object in the constructor won't be tracked because the original constructor runs before
                // the object is proxied.
                let trackedNewProperty = false;
                for (const property in this) {
                    if (!this._changesIgnoredProperties || !this._changesIgnoredProperties.has(property)) {
                        if (Array.isArray(this[property])) {
                            const arrayHistoryIgnored =
                                this._historyIgnoredProperties && this._historyIgnoredProperties.has(property);
                            // We've already filtered out change ignored properties above
                            this[property] = new TrackedArray(
                                proxy,
                                property.toString(),
                                false,
                                arrayHistoryIgnored,
                                this[property]
                            ) as any;
                        }
                        originalValuesMap.set(property, this[property]);
                        changedValuesMap.set(property, this[property]);
                        trackedNewProperty = true;
                    }
                }
                if (trackedNewProperty) {
                    updateIsModified();
                }

                if (onCreateCallback) {
                    onCreateCallback(proxy as unknown as T & ITrackedClass);
                }

                return proxy;

                function updateIsModified() {
                    // If the original value map is not empty, we have some pending changes so this object is considered modified
                    if (trackingState.isModified !== originalValuesMap.size > 0) {
                        trackingState.isModified = originalValuesMap.size > 0;
                        SyncService.registerChangedState(proxy, trackingState.isModified);
                    }
                }

                function commitChanges(target: any, inherited: boolean) {
                    if (trackingState.isModified) {
                        if (!inherited) {
                            // Register the change on the history
                            originalValuesMap.forEach((oldValue, property) => {
                                const historyIgnored =
                                    Reflect.has(target, '_historyIgnoredProperties') &&
                                    Reflect.get(target, '_historyIgnoredProperties').has(property);

                                if (!historyIgnored) {
                                    history.changeEvent(proxy, property, trackingState.baseObject[property], oldValue);
                                }
                            });

                            originalValuesMap.clear();
                            changedValuesMap.clear();
                        }
                        updateIsModified();
                    }
                    SyncService.nodeChangesProcessed(target);
                }

                function setPropertyIsModified(prop: string, modified: boolean) {
                    if (modified) {
                        // When forcing a property as modified, set the original value to null so it stays modified until processed (never matches)
                        originalValuesMap.set(prop, null);
                        changedValuesMap.set(prop, proxy[prop]);
                    } else {
                        originalValuesMap.delete(prop);
                        changedValuesMap.delete(prop);
                    }
                    updateIsModified();
                }

                function destroy(target: any) {
                    if (onDestroy) {
                        onDestroy(proxy as unknown as T & ITrackedClass);
                    }
                    SyncService.registerDeleted(proxy);
                    const inheritedDestroyFunc = Object.getPrototypeOf(target).destroy;
                    if (typeof inheritedDestroyFunc === 'function') {
                        inheritedDestroyFunc();
                    }
                }
            }
        };

        Object.defineProperties(TrackedClass.prototype, {
            addedToChildArray: {
                configurable: false,
                enumerable: false,
                writable: false,
                value: addedToChildArray ? addedToChildArray : () => {},
            },
            removedFromChildArray: {
                configurable: false,
                enumerable: false,
                writable: false,
                value: removedFromChildArray ? removedFromChildArray : () => {},
            },
        });

        return TrackedClass;
    };
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function NodeReference<T>(classRef: Function & { prototype: T }, ...keys: Array<keyof T>) {
    return (target: any, propertyKey: string, parameterIndex?: number) => {
        if (typeof target.nodeReferences === 'undefined') {
            Object.defineProperty(target, 'nodeReferences', {
                value: new Map<string, any>(),
                enumerable: false,
                writable: false,
                configurable: false,
            });
        }

        target.nodeReferences.set(propertyKey, {
            classRef,
            limitKeys: keys.length > 0 ? new Set(keys) : undefined,
        });
    };
}

/** Property decorator to ignore changes on some properties. The node will not be processed if only ignored properties change. */
export function IgnoreChanges(target: any, propertyKey: string, parameterIndex?: number) {
    if (typeof target._changesIgnoredProperties === 'undefined') {
        Object.defineProperty(target, '_changesIgnoredProperties', {
            value: new Map<string, boolean>(),
            enumerable: false,
            writable: false,
            configurable: false,
        });
    }

    target._changesIgnoredProperties.set(propertyKey, true);
}

/** Property decorator to ignore history on some properties. The property changing will not trigger history additions. */
export function IgnoreHistory(target: any, propertyKey: string, parameterIndex?: number) {
    if (typeof target._historyIgnoredProperties === 'undefined') {
        Object.defineProperty(target, '_historyIgnoredProperties', {
            value: new Map<string, boolean>(),
            enumerable: false,
            writable: false,
            configurable: false,
        });
    }

    target._historyIgnoredProperties.set(propertyKey, true);
}

export interface TrackChangesOptions<T> {
    /** Function to run on creation of class instance.
     * @param proxyObj the constructed class instance proxy object.
     */
    onCreate?: (proxyObj: T & ITrackedClass) => void;
    /** Function to run on removal of class instance - called manually, or when removed from an array.
     * @param proxyObj the constructed class instance proxy object.
     */
    onDestroy?: (proxyObj: T & ITrackedClass) => void;
    /** Function to run on undoing removing a class instance from the scene. If we need to undo anything that was destroyed in the onDestroy hook.
     * @param proxyObj the constructed class instance proxy object.
     */
    onUndestroy?: (proxyObj: T & ITrackedClass) => void;
    /** Function to override the changed check behavior for certain properties on this class.
     * If property has not changed compared to the original value, then change queue will be cleared.
     * @param originalValue the old value A to compare against
     * @param newValue the new value B to compare against
     * @param name the property name being changed
     * @returns boolean or undefined. If undefined, will fall back to strict equality check for property.
     */
    isPropertyEqual?: (originalValue: any, newValue: any, name: keyof T) => boolean | undefined;
    /**
     * Function called when an item is added to a property array of the tracked class
     */
    addedToChildArray?: (parent: T & ITrackedClass, item: any, arrayPropertyName: string) => void;
    /**
     * Function called when an item is removed from a property array of the tracked class
     */
    removedFromChildArray?: (parent: T & ITrackedClass, item: any, arrayPropertyName: string) => void;
}
